import { useTranslation } from 'react-i18next';
import { useEventSubscription } from '@pwc/event-hub-ui';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import {
  mapContentType,
  mappedContentTypes,
} from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/utils';
import { PageTitle, TriPane, PageNotViewable, Button } from '@pwc/pwc-deals-ui';
import {
  Constants,
  ErrorUtilities,
  TextUtilities,
  USER_ROLES,
} from '@pwc/pwc-deals-web-common';
import D3Alert from 'common/components/Alert/D3Alert';
import AutoSave from 'common/components/AutoSave.tsx';
import { debounce } from 'lodash';
import ActiveContentHeader from 'common/components/ContentHeaders/ActiveContentHeader';
import FormActions from 'common/components/FormActions/index.tsx';
import {
  AUTO_SAVE_IDLE_ERROR,
  AUTO_SAVE_LOCK_ERROR,
} from 'common/components/GlobalError/constants';
import { FormLayout } from 'common/components/layouts';
import { LinkedContentNavigation } from 'common/components/LinkedContentNavigation/LinkedContentNavigation';
import RichTextBalloonEditorFormikField from 'common/components/RichTextBalloonEditorFormikField/RichTextBalloonEditorFormikField';
import { inlineEditorConfig } from 'common/components/RichTextEditor/CKEditorConfig';
import Separator from 'common/components/Separator';
import SourceInput from 'common/components/SourceInput';
import VersionToggleContainer from 'common/components/VersionToggle/VersionToggleContainer';
import {
  selectDraftVersion,
  selectSharedVersion,
} from 'common/engine/reducers/structuredAnalysisViewSlice';
import {
  getAdjustmentMetadata,
  getAnalysisDescription,
  loadBasisOfPresenation,
  saveDocumentDescriptionThunk,
  setStructuredAnalysesAdjustmentsThunk,
} from 'common/engine/reducers/structuredDocumentDescriptionSlice';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import useMobile from 'common/hooks/useMobile';
import useVersionToggle from 'common/hooks/useVersionToggle';
import LibraryItemSelect from 'features/contentLibrary/LibraryItemSelect/LibraryItemSelect';
import ConnectedContentContext from 'features/narratives/Context/ConnectedContentContext';
import EditConnectedContentNew from 'features/narratives/EditConnectedContentNew/EditConnectedContentNew';
import useAllConnections from 'features/narratives/hooks/useAllConnections';
import { contentIsLinked } from 'features/narratives/narrativesUtilities';
import TagEditor from 'features/tagging/common/TagEditor/TagEditor';
import { getProjectTerritory } from 'features/project/projectSlice';

import { Field, Form } from 'formik';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useDirtyCheck } from '@pwc/auto-save-ui';
import {
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForDdl,
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForNwc,
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForQofE,
  getTableReadyAdjustmentSumsParticipatingInCalculationsForNwc,
  getTableReadyAdjustmentSumsParticipatingInCalculationsForQofE,
  getTableReadyBankDebtAdjustmentSumsForDdl,
  getTableReadyDebtLikeItemAdjustmentSumsForDdl,
  getTableReadyLessCashAdjustmentSumsForDdl,
} from 'common/engine/selectors/adjustmentSelectors';
import { deleteStructuredDocument } from 'common/engine/services/structuredAnalysisService';
import featureFlags, {
  isReportBuilderLivePreviewEnabled,
} from 'common/helpers/featureFlags';
import { getErrorMessageDuplicateTitle } from 'common/helpers/duplicateTitleError';
import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';
import { projectsContentsSearch } from 'features/analyses/analysesService';
import { STRUCTURED_DISPLAY_NAME_BY_CODE } from 'common/engine/utilities/constants';
import { useExactMatchedRoute } from 'common/hooks/routeHookHelpers';
import useTabletOrMobile from 'common/hooks/useTabletOrMobile';
import { showModalError } from 'common/redux/errors/errorsSlice';
import {
  analysesGroupsListPath,
  analysesListPath,
} from 'common/services/urlService';
import TitleFieldsetWithWordCount from 'common/components/TitleFieldWithWordCount/TitleFieldWithWordCount';
import { getEntityId, setEntityId } from 'features/comments/commentsSlice';
import { getProjectId, getProjectName } from '../../project/projectSlice';
import {
  mapDto,
  validateTitle,
} from '../Freeform/FreeformAnalysisForm/FreeformAnalysisFormUtil/FreeformAnalysisFormUtil';
import { getConvertedStructuredAnalysis } from '../Freeform/FreeformAnalysisForm/reportPreviewSupport.ts';
import {
  getLibraryItemsSelector,
  getReportPreviewPropsSelector,
  setReportPreviewProps,
} from '../libraryItemsSlice';
import AnalysisRichtextDescription from '../structured-analysis/common/AnalysisRichtextDescription/AnalysisRichtextDescription';
import styles from '../structured-analysis/common/StructuredAnalysisView/StructuredAnalysisView.module.scss';
import {
  chartForDocumentCode,
  tableForDocumentCode,
} from '../structured-analysis/common/StructuredAnalysisView/structuredComponentToDocumentCodeMap';
import './AnalysisForm.scss';
import NotesInput from './NotesInput';
import { getUserRole } from '../../../common/redux/security/loggedInUser/loggedInUserSlice';

const AnalysisForm = ({
  documentCode = '',
  onCancel,
  onDone,
  onPreview,
  isSaving,
  errorType,
  errors,
  touched,
  isValid,
  children,
  values,
  setFieldValue,
  showLibraryItemPicker = true,
  showLinkedAdjustments,
  showNotes = false,
  projectId,
  parentContentId,
  narratives,
  setNarratives,
  adjustmentOptions,
  targetDocumentOptions,
  dirty: _dirty,
  analysis,
  isEditMode,
  initialPaneTwoAvailable,
  setConnectedContentChanged,
  submitForm,
  handleRedirectToView,
  isSavingFulfilled,
  isSavingRejected,
  ...rest
}) => {
  const isMountedRef = useIsMountedRef();
  const [body, setBody] = useState(analysis.body);
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [insight, setInsight] = useState(analysis.insight);
  const [titleSA, setTitle] = useState(analysis.title);
  const [titleError, setTitleError] = useState(null);
  const TableComponent = tableForDocumentCode(documentCode);
  const ChartComponent = chartForDocumentCode(documentCode);
  const dispatch = useDispatch();
  const [initialForm, setInitialForm] = useState(null);
  const dirtyCheck = useDirtyCheck(initialForm);
  const { libraryItems } = useSelector(getLibraryItemsSelector);
  const adjustmentMetadata = useSelector(getAdjustmentMetadata);
  const userRole = useSelector(getUserRole);
  const isPwcUser = userRole === USER_ROLES.PWC_USER;
  const { t } = useTranslation();
  const {
    isSavingDescription,
    isBasisOfPresentationMode,
    isSavingDescriptionFulfilled,
    basisOfPresentation,
    content,
    libraryItemId,
  } = useSelector(getAnalysisDescription);

  const save = async () => {
    // isBasisOfPresentationMode means the user is editing the form otherwise the user is editing Adjustment description
    if (isBasisOfPresentationMode) {
      await submitForm();
    } else {
      dispatch(
        saveDocumentDescriptionThunk(adjustmentDescription, false, projectId)
      );
      if (_dirty) {
        await submitForm();
      }
    }
  };

  const [connectedLinkedContent, setConnectedLinkedContent] = useState({
    analyses: [],
    hypotheses: [],
  });

  const [connectedLinkedContentLoaded, setConnectedLinkedContentLoaded] =
    useState(false);

  const isAutoSaveReady = values && isValid && connectedLinkedContentLoaded;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentForm = {
    title: values.title,
    body: values.body,
    insight: values.insight,
    tags: values.tags,
    connectedAnalyses: values.connectedAnalyses,
    notes: values.notes,
    source: values.source,
    adjustmentDescription,
    linkedHypotheses: connectedLinkedContent.hypotheses.map(
      (h) => h.contentThreadId
    ),
    linkedAnalyses: connectedLinkedContent.analyses.map(
      (a) => a.contentThreadId
    ),
    linkedAdjustments: values.linkedAdjustments,
  };

  const handleDone = async () => {
    // TODO: this is temporary revert for urgent bug
    // if (dirtyCheck.dataIsDirty(currentForm)) {
    await save();
    // }
    onDone();
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isAutoSaveReady && initialForm === null) {
      setInitialForm({
        title: values.title,
        body: values.body,
        insight: values.insight,
        tags: values.tags,
        connectedAnalyses: values.connectedAnalyses,
        notes: values.notes,
        source: values.source,
        adjustmentDescription: '',
        linkedHypotheses: connectedLinkedContent.hypotheses.map(
          (h) => h.contentThreadId
        ),
        linkedAnalyses: connectedLinkedContent.analyses.map(
          (a) => a.contentThreadId
        ),
        linkedAdjustments: values.linkedAdjustments,
      });
    }
  }, [
    isAutoSaveReady,
    values,
    initialForm,
    connectedLinkedContent.hypotheses,
    connectedLinkedContent.analyses,
  ]);

  useEventSubscription('autosave-save', async () => {
    if (!isValid) {
      return;
    }
    save();
    dirtyCheck.setLastData(currentForm);
  });

  useEventSubscription('autosave-idle', () => {
    handleRedirectToView(AUTO_SAVE_IDLE_ERROR);
  });

  const insightField = {
    name: 'insight',
    value: insight,
  };

  const { allConnections: allRetrievedConnections } = useAllConnections(
    isMountedRef.current
  );

  const draftVersion = useSelector(selectDraftVersion(documentCode));
  const sharedVersion = useSelector(selectSharedVersion(documentCode));
  const commentsEntityId = useSelector(getEntityId);
  const enableVersionToggle = useMemo(
    () =>
      isMountedRef.current && commentsEntityId === analysis?.contentThreadId,
    [isMountedRef, commentsEntityId, analysis?.contentThreadId]
  );

  const { commentCounts } = useVersionToggle(
    enableVersionToggle,
    draftVersion,
    sharedVersion
  );

  const isMobile = useMobile();

  useEffect(() => {
    if (analysis?.contentThreadId && !enableVersionToggle) {
      dispatch(setEntityId(analysis.contentThreadId));
    }
  }, [analysis?.contentThreadId, dispatch, enableVersionToggle]);

  useEffect(() => {
    if (!narratives && allRetrievedConnections) {
      setNarratives(allRetrievedConnections);
    }
  }, [allRetrievedConnections, narratives, setNarratives]);
  const { lastSelectedKFA } = useContext(ConnectedContentContext);

  useMemo(() => {
    if (narratives) {
      const linkedAllConnections = Object.keys(narratives).map((key) => {
        const connectedAnalyses = narratives[key].filter((obj) => {
          const isObj = contentIsLinked(obj.links, parentContentId);

          return isObj;
        });

        return { [key]: connectedAnalyses };
      });

      const objectConnections = Object.assign({}, ...linkedAllConnections);
      setConnectedLinkedContent(objectConnections);
      setConnectedLinkedContentLoaded(true);
      return [];
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    narratives,
    parentContentId,
    setConnectedContentChanged,
    lastSelectedKFA,
  ]);

  const titleField = {
    name: 'title',
    value: titleSA,
  };

  const bodyField = {
    name: 'body',
    value: body,
  };

  useEffect(() => {
    if (isMountedRef.current && isBasisOfPresentationMode) {
      dispatch(
        loadBasisOfPresenation(
          projectId,
          documentCode,
          analysis.contentThreadId,
          analysis.versionNumber,
          false
        )
      );
    }
  }, [
    analysis.contentThreadId,
    analysis.versionNumber,
    dispatch,
    documentCode,
    isMountedRef,
    projectId,
    isBasisOfPresentationMode,
  ]);

  const saveBasisOfPresentation = (newValue) => {
    setBody(newValue);
    setFieldValue(bodyField.name, newValue);
  };

  const saveAdjustmentDescription = (newValue) => {
    setAdjustmentDescription(newValue);
  };

  const handleDescriptionChange = (newValue) => {
    if (isBasisOfPresentationMode) {
      if (body !== newValue) {
        dispatch(
          setReportPreviewProps({
            isDirty: true,
          })
        );
      }

      saveBasisOfPresentation(newValue);
    } else {
      if (adjustmentDescription !== newValue && adjustmentDescription !== '') {
        dispatch(
          setReportPreviewProps({
            isDirty: true,
          })
        );
      }

      saveAdjustmentDescription(newValue);
    }
  };

  const saveAdjustmentLinks = (id, items, sourceAdjustment) => {
    const filteredData = values.linkedAdjustments.filter(
      (i) => i.sourceAdjustment !== sourceAdjustment
    );
    const allData = [...items, ...filteredData];
    setFieldValue(id, allData);
  };

  const handleReportPreview = () => {
    onPreview(values);
  };

  const matchedRoute = useExactMatchedRoute();
  const { path } = matchedRoute;

  const sectionsByStructuredAnalysesType = useMemo(() => {
    const allowedReportPreviewPages = [
      {
        url: '/analyses/quality-of-earnings/edit',
        section: {
          order: 1,
          layout: 'quality-of-earnings',
          positions: null,
        },
      },

      {
        url: 'analyses/net-working-capital/edit',
        section: {
          order: 1,
          layout: 'net-working-capital',
          positions: null,
        },
      },
      {
        url: 'analyses/debt-debt-like/edit',
        section: {
          order: 1,
          layout: 'debt-and-debt-like',
          positions: null,
        },
      },
    ];

    return allowedReportPreviewPages.find((page) => path.endsWith(page.url))
      .section;
  }, [path]);

  const debtLikeItemAdjustmentSums = useSelector(
    getTableReadyDebtLikeItemAdjustmentSumsForDdl
  );

  // DEBT and Debt Like
  const DebtnotParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForDdl
  );
  const participatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsParticipatingInCalculationsForQofE
  );
  const bankDebtAdjustmentSums = useSelector(
    getTableReadyBankDebtAdjustmentSumsForDdl
  );
  const lessCashAdjustmentSums = useSelector(
    getTableReadyLessCashAdjustmentSumsForDdl
  );

  // NET Working Capital
  const NetparticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsParticipatingInCalculationsForNwc
  );
  const NetnotParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForNwc
  );

  // Quality of Earnings
  const notParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForQofE
  );

  const adjustments = useMemo(() => {
    const contentType = mapContentType(analysis.type, analysis.contentSubType);
    if (contentType === mappedContentTypes.QUALITY_OF_EARNINGS) {
      return participatingAdjustmentSums.concat(notParticipatingAdjustmentSums);
    }
    if (contentType === mappedContentTypes.NET_WORKING_CAPITAL) {
      return NetparticipatingAdjustmentSums.concat(
        NetnotParticipatingAdjustmentSums
      );
    }
    if (contentType === mappedContentTypes.DEBT_DEBT_LIKE)
      return bankDebtAdjustmentSums.concat(
        lessCashAdjustmentSums,
        debtLikeItemAdjustmentSums,
        DebtnotParticipatingAdjustmentSums
      );

    return [];
  }, [
    DebtnotParticipatingAdjustmentSums,
    NetnotParticipatingAdjustmentSums,
    NetparticipatingAdjustmentSums,
    analysis.contentSubType,
    analysis.type,
    bankDebtAdjustmentSums,
    debtLikeItemAdjustmentSums,
    lessCashAdjustmentSums,
    notParticipatingAdjustmentSums,
    participatingAdjustmentSums,
  ]);

  const [summaryTable, setSummaryTable] = useState(null);
  const [allParts, setAllParts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const projectTerritory = useSelector(getProjectTerritory);

  const [ebitdaAdjustmentBrdigeChart, setEbitdaAdjustmentBrdigeChart] =
    useState(null);

  const adjustmentDescriptions = useCallback(
    async ({ adjustmentType, adjustmentTitle }) => {
      const contentType = mapContentType(
        analysis.type,
        analysis.contentSubType
      );

      const result = await dispatch(
        setStructuredAnalysesAdjustmentsThunk(
          adjustmentType,
          adjustmentTitle,
          contentType,
          projectId,
          analysis.contentId,
          analysis.versionNumber,
          false
        )
      ).then((result1) => {
        return result1;
      });

      return result;
    },
    [
      analysis.type,
      analysis.contentSubType,
      analysis.contentId,
      analysis.versionNumber,
      dispatch,
      projectId,
    ]
  );

  const analysisSearchTitle = useCallback(
    async (searchTitle) => {
      if (!searchTitle.length) {
        setTitleError(null);
        return false;
      }
      if (isTitleRestricted(searchTitle)) {
        setTitleError(restrictedErrorMessage);
        return false;
      }
      const trimmedTitle = TextUtilities.trimText(searchTitle);
      const err = validateTitle(trimmedTitle);
      if (err.length > 0) {
        setTitleError(err);
        return false;
      }
      try {
        const matchItems = await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        });
        if (
          matchItems &&
          matchItems.length > 0 &&
          TextUtilities.trimText(matchItems[0].title).toLowerCase() ===
            trimmedTitle.toLowerCase() &&
          matchItems[0].contentThreadId !== analysis.contentThreadId
        ) {
          setTitleError(getErrorMessageDuplicateTitle(matchItems[0], t));
          return false;
        }
        setTitleError(null);
      } catch (e) {
        setTitleError(ErrorUtilities.getErrorMessage(e));
        return false;
      }
      return true;
    },
    [projectId, analysis.contentThreadId, t]
  );

  const debouncedSearchTitle = useMemo(
    () => debounce(analysisSearchTitle, 250),
    [analysisSearchTitle]
  );

  const cleanTableFromSVGS = (clonedTable) => {
    const svgs = [
      ...clonedTable.getElementsByClassName('icon fill icon-orange'),
    ];
    const adjustmentControls = [
      ...clonedTable.getElementsByClassName('adjustment-controls-column'),
    ];

    if (svgs && svgs.length) {
      svgs.map((element) => {
        element.remove();
        return svgs;
      });
    }

    if (adjustmentControls) {
      adjustmentControls.map((control) => {
        control.remove();
        return adjustmentControls;
      });
    }

    return clonedTable;
  };

  const [adjustmentTables, setAdjustmentTables] = useState([]);

  const appendEmptyRow = (table) => {
    const tableBody = table.getElementsByTagName('tbody')[0];
    const row = tableBody.insertRow();

    row.setAttribute('role', 'row');
    row.setAttribute('class', 'adjustment final-details-row');
    row.setAttribute('data-testid', 'adjustment');

    const firstRow = tableBody.getElementsByTagName('tr');

    if (firstRow.length > 0 && firstRow[0].cells) {
      for (let i = 0; i < firstRow[0].cells.length - 1; i += 1) {
        if (i === 0) {
          const th = row.insertCell();
          th.outerHTML = '<th></th>';
          th.setAttribute('role', 'rowheader');
          th.setAttribute('class', 'adjustment__heading');
        } else {
          const cell = row.insertCell();
          cell.setAttribute('role', 'cell');
          cell.setAttribute('class', 'financial-value');
        }
      }
    }
  };

  const getAdjustmentTable = (clonedDocument) => {
    const table = clonedDocument.querySelectorAll('table')[0];
    appendEmptyRow(table);
    const updatedTable = cleanTableFromSVGS(table);
    return updatedTable;
  };

  const generateAdjustmentTable = (title, adjustmentIndex) => {
    const adjustementHeaders = document.getElementsByClassName(
      'adjustment-expansion-button-title'
    );

    const adjustmentTitleBtn = adjustementHeaders[adjustmentIndex].parentNode;
    adjustmentTitleBtn.click();

    const clonedDocument = document.cloneNode(true);
    clonedDocument
      .getElementById(`adjustment-group__toggle-button-${title}`)
      .click();

    const updatedTable = getAdjustmentTable(clonedDocument);

    setAdjustmentTables((existingArray) => [...existingArray, updatedTable]);

    if (projectTerritory === Constants.TERRITORY_CODES.UNITED_KINGDOM) {
      if (
        Array.from(adjustementHeaders)[
          Array.from(adjustementHeaders).length - 1
        ]?.innerText === title
      ) {
        const allTables = document.querySelectorAll('table');
        const clonedTable = allTables[0]?.cloneNode(true);
        const cleanedTableFromElements = cleanTableFromSVGS(clonedTable);

        setSummaryTable(cleanedTableFromElements);
        Array.from(adjustementHeaders).map((eachItem) => {
          document
            .getElementById(
              `adjustment-group__toggle-button-${eachItem?.innerText}`
            )
            .click();
          return updatedTable;
        });
      }
    } else {
      document
        .getElementById(`adjustment-group__toggle-button-${title}`)
        .click();
    }

    return updatedTable;
  };

  useEffect(() => {
    const fetchAdjustments = async () => {
      return (
        (await adjustments) &&
        adjustments.length &&
        adjustments.reduce(async (accum, element, index) => {
          const adjustmentsArray = await accum;
          generateAdjustmentTable(element.adjustmentType, index);
          if (
            element.adjustments &&
            Object.keys(element.adjustments).length > 1
          ) {
            Promise.all(
              Object.keys(element.adjustments)
                .filter((el) => el !== 'totals')
                .map(async (form) => {
                  const result = await adjustmentDescriptions({
                    adjustmentType: element.adjustmentType,
                    adjustmentTitle: form,
                  });
                  const placeholder = result.data
                    ? result.data
                    : `<div><p>${result.content.plainText}</p></div>`;
                  const description = {
                    adjustmentHeader: element.adjustmentType,
                    adjustmentTitle: form,
                    adjustmentDescription: placeholder.toString(),
                  };
                  adjustmentsArray.push(description);
                })
            );
          }
          return adjustmentsArray;
        }, Promise.resolve([]))
      );
    };
    fetchAdjustments().then((result) => {
      if (result) {
        setDescriptions(result);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustmentDescriptions, adjustments]);

  useEffect(() => {
    const bridgeContainer = document.getElementsByClassName(
      'bridge-chart-container'
    );
    const allTables = document.querySelectorAll('table');
    if (allTables && adjustments && descriptions && adjustmentTables) {
      const clonedTable = allTables[0]?.cloneNode(true);
      if (
        allTables &&
        allTables.length &&
        clonedTable &&
        projectTerritory !== Constants.TERRITORY_CODES.UNITED_KINGDOM
      ) {
        const cleanedTableFromElements = cleanTableFromSVGS(clonedTable);

        setSummaryTable(cleanedTableFromElements);
      }

      // selected adjustment Group = adjustmentHeader, Key = adjustmentTitle
      // the description is in adjustmentDescription
      const { adjustmentKey } = adjustmentMetadata;

      const allAdjustments = adjustments.reduce((accum, element, index) => {
        if (
          element.adjustments &&
          Object.keys(element.adjustments).length > 1
        ) {
          accum.push({
            type: 'adjustment-table',
            key: element.adjustmentType,
            name: element.adjustmentType,
            data: adjustmentTables[index]?.outerHTML,
          });

          const description = Object.keys(element.adjustments)
            .filter((el) => el !== 'totals')
            .map((form) => {
              const findDescription = descriptions
                // we do not handle duplicates in the adjustmentTitles here, that would fail; hoping they are all
                // unique for now
                .filter(
                  (el) =>
                    adjustmentDescription === '' ||
                    el.adjustmentTitle !== adjustmentKey
                )
                .concat(
                  adjustmentDescription === ''
                    ? {}
                    : {
                        adjustmentTitle: adjustmentKey,
                        adjustmentDescription,
                      }
                )
                .find((el) => el.adjustmentTitle === form);

              return `<div><strong>${form}</strong>${
                findDescription?.adjustmentDescription || ''
              }</div>`;
            });

          if (description) {
            accum.push({
              type: 'adjustment-description',
              key: element.adjustmentType,
              name: element.adjustmentType,
              data: description.join(''),
            });
          }
        }

        return accum;
      }, []);

      setAllParts(allAdjustments);
    }
    if (bridgeContainer && bridgeContainer.length) {
      const chart = bridgeContainer[0].querySelector('svg');

      setEbitdaAdjustmentBrdigeChart(chart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adjustments,
    descriptions,
    descriptions.length,
    body,
    adjustmentDescription,
  ]);

  const history = useHistory();

  const liveReportPreviewData = {
    ...mapDto(
      values.title,
      values.insight, // insight
      values.body,
      [], // currentTags
      sectionsByStructuredAnalysesType,
      analysis?.contentThreadId,
      values.notes,
      values.source
    ),
    allParts,
    tags: null,
    linkedHypotheses: null,
    linkedAnalyses: null,
  };
  const getPayload = () =>
    getConvertedStructuredAnalysis({
      title: values.title,
      pageId: '.form-content-container',
      analysis: { ...analysis, tags: null },
      insightId: "[data-testid='text-area-insight']",
      descriptionId: "[data-testid='text-area-body']",
      libraryItems,
      territory: projectTerritory,
      tableSummary: summaryTable,
      notes: values.notes,
      source: values.source,
      chart: ebitdaAdjustmentBrdigeChart,
      structuredAnalysesSection: [sectionsByStructuredAnalysesType],
      allParts: allParts
        .filter((x) => x.key !== '')
        .concat(allParts.filter((x) => x.key === '')),
      basisOfPresentation,
      projectId,
      isPwcUser,
    });
  const liveReportPreview = useLiveReportPreview();

  liveReportPreview.getData = () => liveReportPreviewData;
  liveReportPreview.getPayload = getPayload;
  liveReportPreview.getProjectId = getProjectId;
  liveReportPreview.getReportPreviewPropsSelector =
    getReportPreviewPropsSelector;
  liveReportPreview.setReportPreviewProps = setReportPreviewProps;
  liveReportPreview.useTabletOrMobile = useTabletOrMobile;

  const projectName = useSelector(getProjectName);

  useEffect(() => {
    if (isReportBuilderLivePreviewEnabled) {
      dispatch(
        setReportPreviewProps({
          contentThreadId: analysis.contentThreadId,
          isShared:
            analysis.versionStatus === Constants.VERSION_STATUS.SHARED.value,
          territoryName: projectTerritory,
        })
      );
    }
  }, [
    analysis.contentThreadId,
    analysis.versionStatus,
    dispatch,
    projectTerritory,
  ]);

  // Fix handle adjustment save AB#128619
  useEffect(() => {
    handleDescriptionChange(
      content.richText || (content.plainText && content.plainText?.data)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItemId]);

  if (isMobile) {
    return (
      <PageNotViewable>
        <Link to={analysesListPath(projectId)}>
          <Button className="full-width-mobile">Back to analysis page</Button>
        </Link>
      </PageNotViewable>
    );
  }

  const handleDelete = async (callback) => {
    try {
      await deleteStructuredDocument(projectId, documentCode);
      callback();
      history.push(analysesGroupsListPath(projectId));
    } catch (e) {
      callback();
      dispatch(
        showModalError(Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE)
      );
    }
  };

  const activeContent = (isBottom) => (
    <FormActions
      position={isBottom ? 'bottom' : 'top'}
      contentType="analyses"
      isSaving={isSaving || isSavingDescription}
      isSavingFulfilled={isSavingFulfilled || isSavingDescriptionFulfilled}
      isSavingRejected={isSavingRejected}
      isCreateMode={false}
      cancelAction={onCancel}
      doneAction={handleDone}
      reportAction={handleReportPreview}
      deleteAction={handleDelete}
      isReportDisabled={isSaving || isSavingDescription}
      isDoneDisabled={isSaving || isSavingDescription || titleError}
      isDeleteDisabled={isSaving || isSavingDescription}
      isPreviewHidden
      projectName={projectName}
      itemTitle={analysis.title}
    />
  );

  const headerComponent = (
    <div className="toggle-version-container-edit-mode">
      <VersionToggleContainer
        mode="edit"
        isEntitySharedWithClient={false}
        projectId={projectId}
        config={{
          commentCounts,
          draftVersion,
          sharedVersion,
        }}
      />
      <ActiveContentHeader>
        <div className="analysis-form-top-actions">{activeContent(false)}</div>
      </ActiveContentHeader>
    </div>
  );

  const contentFooter = activeContent(true);

  if (analysis.contentThreadId) {
    // props that should not be on form
    // eslint-disable-next-line no-unused-vars
    const { onSaveText, standbyText, previewText, ...propsForm } = rest;

    return (
      <AutoSave
        projectId={projectId}
        contentThreadId={analysis.contentThreadId}
        isReady={isAutoSaveReady}
        isValid={isValid}
        initialData={initialForm}
        getData={() => {
          return currentForm;
        }}
      >
        {({ acquireLockState: { isError, error } }) => {
          if (isError || error) {
            handleRedirectToView(AUTO_SAVE_LOCK_ERROR);
          }
          return (
            <div className="structured-analysis-form-layout edit-sa-scroll-component">
              <Form
                data-testid="analysis-form"
                className="analysis-form"
                {...propsForm}
              >
                <FormLayout
                  footerComponent={contentFooter}
                  headerComponent={headerComponent}
                >
                  <div className="structured-analysis-view">
                    <div className="structured-analysis-view__header">
                      <div
                        className="structured-analysis-view__analysis__contents"
                        data-testid="structured-analysis-view"
                      >
                        <div
                          className={styles.header}
                          data-testid="structured-analysis-header"
                        >
                          {featureFlags?.isStructuredAnalysesTitleEditable ? (
                            <TitleFieldsetWithWordCount
                              field={titleField}
                              form={{
                                setFieldValue: (_, newValue) => {
                                  setTitle(newValue);
                                  debouncedSearchTitle(newValue);
                                  setFieldValue(titleField.name, newValue);
                                },
                              }}
                              name="title"
                              placeholder="Title (required)"
                              suggestedLength={35}
                              hideWordCountWhenInactive
                              errorMessage={titleError}
                              dataTestId="text-area-title"
                            />
                          ) : (
                            <PageTitle
                              title={
                                STRUCTURED_DISPLAY_NAME_BY_CODE[documentCode]
                              }
                            />
                          )}
                          <RichTextBalloonEditorFormikField
                            field={insightField}
                            form={{
                              setFieldValue: (_, newValue) => {
                                setInsight(newValue);
                                setFieldValue(insightField.name, newValue);
                              },
                            }}
                            label="Insight (optional)"
                            name="insight"
                            dataTestId="text-area-insight"
                            suggestedLength={420}
                            hideWordCountWhenInactive
                            withContainer
                            containerLabel="Insight"
                            containerIcon="light-bulb"
                          />
                        </div>
                        <div className="structured-analysis-view__linked-navigaton-container">
                          <LinkedContentNavigation
                            projectId={projectId}
                            connectedAnalyses={connectedLinkedContent.analyses}
                            connectedKfas={connectedLinkedContent.hypotheses}
                            hypothesisIndex={1}
                            parentContentId={analysis.contentId}
                            currentContent={analysis}
                            parentAllRetrievedConnections={narratives}
                            useParentConnections
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="rich-text-editor">
                    <TriPane initialPaneTwoAvailable={initialPaneTwoAvailable}>
                      {children}
                      {errors.title && touched.title && (
                        <div>{errors.title}</div>
                      )}
                      <div>
                        <div
                          className="tri-pane-container"
                          data-testid="tri-pane-display"
                        >
                          <TriPane.HeaderContent>
                            {undefined}
                          </TriPane.HeaderContent>
                          <TriPane.PaneOne>
                            <div className="scrollable structured-table">
                              <TableComponent
                                contentId={analysis.contentThreadId || 0}
                                contentVersionNumber={
                                  analysis.versionNumber || 0
                                }
                                projectId={projectId}
                                isEditMode={isEditMode}
                                adjustmentOptions={adjustmentOptions}
                                targetDocumentOptions={targetDocumentOptions}
                                selectedLinkedAdjustments={
                                  values.linkedAdjustments
                                }
                                setFieldValue={saveAdjustmentLinks}
                                showLinkedAdjustments={showLinkedAdjustments}
                              />
                            </div>
                          </TriPane.PaneOne>
                          <TriPane.PaneTwo>
                            <ChartComponent key="edit-structure-chart" />
                          </TriPane.PaneTwo>
                          <TriPane.PaneThree>
                            <AnalysisRichtextDescription
                              projectId={projectId}
                              isViewingSharedVersion={
                                analysis.versionStatus ===
                                Constants.VERSION_STATUS.SHARED.value
                              }
                              lightModeVariant
                              onChange={handleDescriptionChange}
                              config={inlineEditorConfig}
                              documentCode={documentCode}
                            />
                          </TriPane.PaneThree>
                        </div>
                        <div className="analysis-form-extras input-container">
                          <div className="source-editor">
                            <Field
                              name="source"
                              component={SourceInput}
                              showEditIcon
                              labelIcon="docs"
                              includeSeparator
                              placeholder="Per2 management information and PwC analysis"
                            />
                          </div>
                          {showNotes && (
                            <div className="notes-editor">
                              <Field name="notes" component={NotesInput} />
                            </div>
                          )}

                          {showLibraryItemPicker && (
                            <Field
                              name="libraryItemId"
                              component={LibraryItemSelect}
                            />
                          )}
                          <TagEditor projectId={projectId} showSeparator />

                          <EditConnectedContentNew
                            projectId={projectId}
                            parentContentId={parentContentId}
                            setCurrentlyVisibleConnections={setNarratives}
                            allConnections={narratives}
                            connectedContentType="hypotheses"
                            setConnectedContentChanged={
                              setConnectedContentChanged
                            }
                            currentAnalysis={analysis}
                            pageContainerName="structured-analysis-form-layout"
                          />
                          <EditConnectedContentNew
                            lastItem
                            projectId={projectId}
                            parentContentId={parentContentId}
                            setCurrentlyVisibleConnections={setNarratives}
                            allConnections={narratives}
                            connectedContentType="analyses"
                            setConnectedContentChanged={
                              setConnectedContentChanged
                            }
                            currentAnalysis={analysis}
                            pageContainerName="structured-analysis-form-layout"
                          />
                        </div>
                        {errorType && (
                          <>
                            <div className="analysis-form-actions-separator mt-1">
                              <Separator />
                            </div>
                            <D3Alert configType={errorType} />
                          </>
                        )}
                      </div>
                    </TriPane>
                  </div>
                </FormLayout>
              </Form>
            </div>
          );
        }}
      </AutoSave>
    );
  }
  return <></>;
};

export default AnalysisForm;
